import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  beginSignIn,
  beginSignOut,
  hasSignedOut,
  sessionGeneration,
  sessionIsCurrent,
} from '@/auth/sessionLifecycle';
import { readBranchSelection, rememberBranchSelection } from '@/auth/branchSelection';
import { b2cConfigured, getB2CAccessToken, renewB2CAccessToken, signOutOfB2C } from '@/auth/b2c';
import {
  fetchAuthorizedBranches,
  type BranchOption,
  fetchMeLinks,
  validateActiveUser,
  withMeRoles,
  webRolesOf,
  type UserAccess,
} from '@/auth/userAccess';
import { fetchWebMenus, type FabricaMenus } from '@/auth/fabricaMenus';
import { GlobalAction, SESSION_STORAGE_KEY } from '@/global/globalState';
import { ROLE_STORAGE_KEY } from '@/routes/roles';
import { leaveAdminMode } from '@/auth/adminMode';
import { queryClient } from '@/lib/queryClient';
import { rememberSchoolList, resetSchoolScope } from '@/api/schoolScope';
import { authorizedSchoolsKey, authorizedBranchesKey } from './school';
import { useGlobalState } from './useGlobalState';

/** The restore in flight, if any. */
let restoring: Promise<UserAccess | null> | null = null;

export interface UserAccessApi {
  access: UserAccess | null;
  signIn: (b2cToken: string) => Promise<UserAccess>;
  signInWithSchoolAccount: () => Promise<UserAccess>;
  restore: () => Promise<UserAccess | null>;
  available: boolean;
  switchOrganization: (organizationId: number) => Promise<UserAccess>;
  switchBranch: (branch: BranchOption) => Promise<UserAccess & { menus: FabricaMenus | null }>;
  signOut: () => Promise<void>;
}
interface LoadOptions {
  organizationId?: number;
  branchId?: number;
  restore?: boolean;
}
let latestLoad = 0;

/** Resolve and commit the whole access context together; a failed switch leaves the old context intact. */
export function useUserAccess(): UserAccessApi {
  const { state, dispatch } = useGlobalState();
  const load = useCallback(
    async (token: string, options: LoadOptions = {}, attempt = sessionGeneration()) => {
      const loadId = ++latestLoad;
      const current = () => sessionIsCurrent(attempt) && loadId === latestLoad;
      const assertCurrent = () => {
        if (!current()) throw new Error('This sign-in or branch switch was cancelled.');
      };
      const saved = options.restore ? readBranchSelection() : null;
      let access = await validateActiveUser(
        token,
        options.organizationId ?? saved?.organizationId,
        options.branchId,
      );
      assertCurrent();
      const branches = access.roles.length
        ? await fetchAuthorizedBranches(
            token,
            access.activeOrganization.organizationId,
            access.branchId,
          )
        : [];
      assertCurrent();
      const schools = branches.flatMap((b) => (b.school ? [b.school] : []));
      const savedBranch =
        saved &&
        saved.userId === access.user.userId &&
        saved.organizationId === access.activeOrganization.organizationId
          ? branches.find((b) => b.branchId === saved.branchId)
          : undefined;
      const branch =
        options.branchId !== undefined
          ? branches.find((b) => b.branchId === options.branchId)
          : (savedBranch ?? branches.find((b) => b.branchId === access.branchId) ?? branches[0]);
      if (options.branchId !== undefined && !branch)
        throw new Error('This branch is no longer available to your account.');
      const school = branch?.school;
      if (branch && branch.branchId !== access.branchId)
        access = await validateActiveUser(
          token,
          access.activeOrganization.organizationId,
          branch.branchId,
        );
      assertCurrent();
      const strict = options.branchId !== undefined || Boolean(state.access);
      const links = await fetchMeLinks(
        token,
        access.activeOrganization.organizationId,
        access.branchId,
      ).catch((error: unknown) => {
        if (strict) throw error;
        return undefined;
      });
      const menus = await fetchWebMenus(
        token,
        access.roles.map((r) => r.roleId),
        {
          organizationId: access.activeOrganization.organizationId,
          branchId: access.branchId,
        },
      ).catch((error: unknown) => {
        if (strict) throw error;
        return null;
      });
      assertCurrent();
      const enriched = { ...access, roles: withMeRoles(access.roles, links?.roles) };
      if (options.branchId !== undefined && !webRolesOf(enriched, menus).length)
        throw new Error('Your roles in this branch do not provide access to the web workspace.');
      await queryClient.cancelQueries();
      assertCurrent();
      // No old request or unscoped query cache can supply records under the new header.
      resetSchoolScope();
      rememberSchoolList(schools);
      flushSync(() => {
        queryClient.clear();
        queryClient.setQueryData(
          authorizedBranchesKey(access.activeOrganization.organizationId, access.user.userId),
          branches,
        );
        queryClient.setQueryData(
          authorizedSchoolsKey(access.activeOrganization.organizationId, access.user.userId),
          schools,
        );
        dispatch({
          type: GlobalAction.AccessLoaded,
          access,
          token,
          menus,
          links,
          schoolId: school?.schoolId ?? null,
        });
      });
      rememberBranchSelection(
        branch
          ? {
              userId: access.user.userId,
              organizationId: access.activeOrganization.organizationId,
              branchId: branch.branchId,
              schoolId: school?.schoolId ?? null,
            }
          : null,
      );
      return { ...enriched, menus };
    },
    [dispatch, state.access],
  );

  const switchOrganization = useCallback(
    (organizationId: number) => {
      if (!state.token) return Promise.reject(new Error('Sign in first.'));
      return load(state.token, { organizationId });
    },
    [load, state.token],
  );

  const switchBranch = useCallback(
    async (branch: BranchOption) => {
      if (!state.token || !state.access) throw new Error('Sign in first.');
      if (queryClient.isMutating())
        throw new Error('Wait for the current save to finish before switching branches.');
      dispatch({ type: GlobalAction.Busy, label: 'Switching branch…' });
      try {
        return await load(state.token, {
          organizationId: state.access.activeOrganization.organizationId,
          branchId: branch.branchId,
        });
      } finally {
        dispatch({ type: GlobalAction.Idle });
      }
    },
    [dispatch, load, state.token, state.access],
  );

  const signOut = useCallback(async () => {
    beginSignOut();
    rememberBranchSelection(null);
    resetSchoolScope();
    flushSync(() => dispatch({ type: GlobalAction.LoggedOut }));
    void queryClient.cancelQueries();
    queryClient.clear();
    leaveAdminMode();
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      localStorage.removeItem(ROLE_STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
    try {
      await signOutOfB2C();
    } catch {
      window.location.replace('/signin');
    }
  }, [dispatch]);

  const signInWithSchoolAccount = useCallback(async () => {
    const attempt = beginSignIn();
    return load(await getB2CAccessToken(), {}, attempt);
  }, [load]);
  const restore = useCallback(() => {
    // One restore at a time: SessionRestore and the Fabrica platform (which waits on it) share the attempt.
    restoring ??= (async () => {
      if (hasSignedOut()) return null;
      const attempt = sessionGeneration();
      const token = await renewB2CAccessToken();
      return token && sessionIsCurrent(attempt)
        ? load(token, { restore: true }, attempt).catch(() => null)
        : null;
    })().finally(() => {
      restoring = null;
    });
    return restoring;
  }, [load]);

  return {
    access: state.access,
    signIn: (token) => load(token, {}, beginSignIn()),
    signInWithSchoolAccount,
    restore,
    available: b2cConfigured,
    switchOrganization,
    switchBranch,
    signOut,
  };
}
